import { useMemo } from 'react';
import { Trans } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import { useShowToast } from '@/design_system/Toast';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAdd from '@/icons/Add.svg';
import { ConditionFormFromGet, useCreateConditionFormAnswer } from '@/models/conditionForm';
import { createBEMClasses } from '@/utils/classname';

import { ConditionAnswer } from './ConditionAnswer';
import { type ConditionFormQuestion } from './ConditionQuestion';

const { element } = createBEMClasses('settings__condition-form__question');

export const ConditionQuestionAnswers = ({
  form,
  question,
}: {
  form: ConditionFormFromGet;
  question: ConditionFormQuestion;
}) => {
  const showToast = useShowToast();
  const { mutateAsync: createConditionFormAnswer, isPending: isCreateConditionFormAnswerPending } =
    useCreateConditionFormAnswer();

  const sortedAnswers = useMemo(
    () =>
      [...question.answers].sort(
        (a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
      ),
    [question.answers]
  );

  const handleAddAnswer = async () => {
    try {
      await createConditionFormAnswer({
        conditionFormId: form.id,
        questionId: question.id,
      });
    } catch {
      showToast({
        text: (
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answers.add.error">
            Could not add an answer. Please try again.
          </Trans>
        ),
        type: 'error',
      });

      throw new Error('Add answer failed');
    }
  };

  return (
    <div className={element('answers')}>
      <Stack
        row
        alignItems="center"
        justifyContent="space-between"
        className={element('answers__label-row')}
      >
        <Paragraph weight="medium" className={element('answers__label')}>
          {question.type === 'multi-quantifiable-select' ? (
            <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.options">
              Options:
            </Trans>
          ) : (
            <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answers">
              Answers:
            </Trans>
          )}

          {sortedAnswers.length === 0 && (
            <>
              {' '}
              <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answers.none">
                None
              </Trans>
            </>
          )}
        </Paragraph>

        <Button
          variant="link-subtle"
          size="small"
          isLoading={isCreateConditionFormAnswerPending}
          onPress={handleAddAnswer}
        >
          <IconAdd />
          {question.type === 'multi-quantifiable-select' ? (
            <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.options.add">
              Add option
            </Trans>
          ) : (
            <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answers.add">
              Add answer
            </Trans>
          )}
        </Button>
      </Stack>

      {sortedAnswers.map((answer) => (
        <ConditionAnswer key={answer.id} form={form} question={question} answer={answer} />
      ))}
    </div>
  );
};
